import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "@/context/AuthContext";
import { useSites } from "@/context/SiteContext";
import Shell from "@/components/Shell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, LineChart, Line,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import { FileXls, FilePdf, FunnelSimple } from "@phosphor-icons/react";

const COLORS = ["#1B4776", "#8595B4", "#059669", "#95C11F", "#DC2626", "#7C3AED", "#0891B2"];
const ALL = "__all__";

// Küçük "filtre şeridi" yardımcı bileşen
const FilterStrip = ({ children }) => (
  <div className="flex flex-wrap items-end gap-3 pb-4 mb-4 border-b border-slate-200">
    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold text-slate-500">
      <FunnelSimple size={12} weight="fill" />
      Filtre
    </div>
    {children}
  </div>
);

export default function Reports() {
  const { activeSiteId, siteParams } = useSites();
  const [timeline, setTimeline] = useState([]);
  const [bySupplier, setBySupplier] = useState([]);
  const [byMaterial, setByMaterial] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [materials, setMaterials] = useState([]);

  // İki farklı filtre grubu — kart bazlı
  const [tlFilter, setTlFilter] = useState({ material: ALL });
  const [bsFilter, setBsFilter] = useState({ supplier: ALL, material: ALL });
  // Global tarih aralığı — tüm kartları ve export'ı etkiler
  const [dateRange, setDateRange] = useState({ from: "", to: "" });

  // Karta özgü backend istekleri
  const _dr = () => {
    const p = {};
    if (dateRange.from) p.date_from = dateRange.from;
    if (dateRange.to) p.date_to = dateRange.to;
    return p;
  };
  const fetchTimeline = async () => {
    const params = { ...siteParams(), ..._dr() };
    if (tlFilter.material !== ALL) params.material_name = tlFilter.material;
    const { data } = await axios.get(`${API}/reports/timeline`, { params });
    setTimeline(data);
  };
  const fetchBySupplier = async () => {
    const params = { ...siteParams(), ..._dr() };
    if (bsFilter.supplier !== ALL) params.supplier_id = bsFilter.supplier;
    if (bsFilter.material !== ALL) params.material_name = bsFilter.material;
    const { data } = await axios.get(`${API}/reports/by-supplier`, { params });
    setBySupplier(data);
  };
  const fetchByMaterial = async () => {
    const params = { ...siteParams(), ..._dr() };
    const { data } = await axios.get(`${API}/reports/by-material`, { params });
    setByMaterial(data);
  };
  const fetchLookups = async () => {
    const [s, m] = await Promise.all([
      axios.get(`${API}/suppliers`),
      axios.get(`${API}/reports/materials-distinct`, { params: siteParams() }),
    ]);
    setSuppliers(s.data);
    setMaterials(m.data);
  };

  useEffect(() => {
    fetchLookups();
    fetchTimeline();
    fetchBySupplier();
    fetchByMaterial();
  }, [activeSiteId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchTimeline(); }, [tlFilter, dateRange]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { fetchBySupplier(); }, [bsFilter, dateRange]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { fetchByMaterial(); }, [dateRange]); // eslint-disable-line react-hooks/exhaustive-deps

  // Export URL'lerini aktif filtrelerle oluştur (Firma Bazında filtreleri baz alınır — en kapsamlı)
  const exportUrl = (kind) => {
    const p = new URLSearchParams(siteParams());
    if (bsFilter.supplier !== ALL) p.set("supplier_id", bsFilter.supplier);
    if (bsFilter.material !== ALL) p.set("material_name", bsFilter.material);
    else if (tlFilter.material !== ALL) p.set("material_name", tlFilter.material);
    if (dateRange.from) p.set("date_from", dateRange.from);
    if (dateRange.to) p.set("date_to", dateRange.to);
    if (kind === "pdf") p.set("report_kind", "report");
    return `${API}/export/materials.${kind}?${p.toString()}`;
  };

  const detay = useMemo(() => bySupplier, [bySupplier]);

  return (
    <Shell
      title="Raporlar"
      actions={
        <>
          <a href={exportUrl("xlsx")} data-testid="rep-export-xlsx">
            <Button variant="outline" className="rounded-none sm:rounded-sm border-slate-300">
              <FileXls size={16} className="mr-2" /> Excel
            </Button>
          </a>
          <a href={exportUrl("pdf")} data-testid="rep-export-pdf">
            <Button className="rounded-none sm:rounded-sm bg-[#1B4776] hover:bg-[#153859] text-white">
              <FilePdf size={16} className="mr-2" /> PDF
            </Button>
          </a>
        </>
      }
    >
      <div className="grid grid-cols-1 gap-4">
        {/* Global tarih aralığı */}
        <Card className="p-4 rounded-none sm:rounded-sm border-slate-200 shadow-sm" data-testid="rep-daterange">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold text-slate-500 self-center pt-3">
              <FunnelSimple size={12} weight="fill" />
              Tarih Aralığı
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Başlangıç</Label>
              <Input type="date" value={dateRange.from}
                     onChange={(e) => setDateRange(r => ({ ...r, from: e.target.value }))}
                     data-testid="rep-date-from"
                     className="mt-1 rounded-none sm:rounded-sm w-44" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Bitiş</Label>
              <Input type="date" value={dateRange.to}
                     onChange={(e) => setDateRange(r => ({ ...r, to: e.target.value }))}
                     data-testid="rep-date-to"
                     className="mt-1 rounded-none sm:rounded-sm w-44" />
            </div>
            <div className="flex flex-wrap gap-1.5 pt-3">
              {[
                { label: "Son 7 gün", days: 7 },
                { label: "Son 30 gün", days: 30 },
                { label: "Bu ay", key: "month" },
                { label: "Bu yıl", key: "year" },
              ].map(preset => (
                <button
                  key={preset.label}
                  type="button"
                  onClick={() => {
                    const now = new Date();
                    let from, to;
                    to = now.toISOString().slice(0, 10);
                    if (preset.days) {
                      const d = new Date(now); d.setDate(d.getDate() - preset.days);
                      from = d.toISOString().slice(0, 10);
                    } else if (preset.key === "month") {
                      from = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
                    } else if (preset.key === "year") {
                      from = new Date(now.getFullYear(), 0, 1).toISOString().slice(0, 10);
                    }
                    setDateRange({ from, to });
                  }}
                  data-testid={`rep-preset-${preset.label.replace(/\s+/g,'-')}`}
                  className="text-[10px] uppercase tracking-widest font-bold text-[#1B4776] border border-slate-300 hover:bg-slate-100 px-2 py-1.5"
                >{preset.label}</button>
              ))}
              {(dateRange.from || dateRange.to) && (
                <button
                  type="button"
                  onClick={() => setDateRange({ from: "", to: "" })}
                  data-testid="rep-date-clear"
                  className="text-[10px] uppercase tracking-widest font-bold text-red-600 hover:bg-red-50 px-2 py-1.5"
                >Temizle</button>
              )}
            </div>
          </div>
        </Card>

        {/* 1) Zaman Çizelgesi — malzeme filtresi */}
        <Card className="p-6 rounded-none sm:rounded-sm border-slate-200 shadow-sm" data-testid="rep-timeline">
          <div className="flex items-baseline justify-between mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500">Zaman Serisi</div>
              <div className="text-lg font-bold text-slate-900 font-heading">Tarihe Göre Girişler</div>
            </div>
            <div className="text-xs text-slate-500">{timeline.length} veri noktası</div>
          </div>
          <FilterStrip>
            <div className="w-full sm:w-72">
              <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Malzeme Tipi</Label>
              <Select value={tlFilter.material} onValueChange={(v) => setTlFilter({ material: v })}>
                <SelectTrigger className="mt-1 rounded-none sm:rounded-sm" data-testid="tl-filter-material">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>Tümü</SelectItem>
                  {materials.map((m) => (
                    <SelectItem key={m} value={m}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {tlFilter.material !== ALL && (
              <button
                type="button"
                onClick={() => setTlFilter({ material: ALL })}
                data-testid="tl-filter-clear"
                className="text-xs text-slate-500 hover:text-slate-900 underline"
              >Temizle</button>
            )}
          </FilterStrip>
          <div style={{ width: "100%", height: 320 }}>
            <ResponsiveContainer>
              <LineChart data={timeline}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="qty" name="Miktar" stroke="#1B4776" strokeWidth={2.5} />
                <Line type="monotone" dataKey="count" name="Adet" stroke="#8595B4" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* 2) Firma Bazında Toplam — çift filtre (Firma + Malzeme) */}
        <Card className="p-6 rounded-none sm:rounded-sm border-slate-200 shadow-sm" data-testid="rep-by-supplier">
          <div className="flex items-baseline justify-between mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500">Firma Analizi</div>
              <div className="text-lg font-bold text-slate-900 font-heading">Firma Bazında Toplam</div>
            </div>
            <div className="text-xs text-slate-500">{bySupplier.length} firma</div>
          </div>
          <FilterStrip>
            <div className="w-full sm:w-72">
              <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Firma</Label>
              <Select value={bsFilter.supplier} onValueChange={(v) => setBsFilter(f => ({ ...f, supplier: v }))}>
                <SelectTrigger className="mt-1 rounded-none sm:rounded-sm" data-testid="bs-filter-supplier">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>Tümü</SelectItem>
                  {suppliers.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-full sm:w-72">
              <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Malzeme Tipi</Label>
              <Select value={bsFilter.material} onValueChange={(v) => setBsFilter(f => ({ ...f, material: v }))}>
                <SelectTrigger className="mt-1 rounded-none sm:rounded-sm" data-testid="bs-filter-material">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>Tümü</SelectItem>
                  {materials.map((m) => (
                    <SelectItem key={m} value={m}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {(bsFilter.supplier !== ALL || bsFilter.material !== ALL) && (
              <button
                type="button"
                onClick={() => setBsFilter({ supplier: ALL, material: ALL })}
                data-testid="bs-filter-clear"
                className="text-xs text-slate-500 hover:text-slate-900 underline"
              >Temizle</button>
            )}
          </FilterStrip>
          <div style={{ width: "100%", height: 340 }}>
            <ResponsiveContainer>
              <BarChart data={bySupplier}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="supplier" tick={{ fontSize: 10 }} interval={0} angle={-25} textAnchor="end" height={90} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend />
                <Bar dataKey="count" name="Giriş Sayısı" fill="#1B4776" />
                <Bar dataKey="qty" name="Toplam Miktar" fill="#95C11F" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* 3) Malzeme Dağılımı */}
        <Card className="p-6 rounded-none sm:rounded-sm border-slate-200 shadow-sm" data-testid="rep-by-material">
          <div className="flex items-baseline justify-between mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500">Malzeme Dağılımı</div>
              <div className="text-lg font-bold text-slate-900 font-heading">En Çok Gelen Malzemeler</div>
            </div>
            <div className="text-xs text-slate-500">İlk 7 kalem</div>
          </div>
          <div style={{ width: "100%", height: 340 }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie data={byMaterial.slice(0, 7)} dataKey="qty" nameKey="material" outerRadius={120} label>
                  {byMaterial.slice(0, 7).map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* 4) Detay Tablo — Firma Toplamları */}
        <Card className="p-6 rounded-none sm:rounded-sm border-slate-200 shadow-sm" data-testid="rep-detail">
          <div className="flex items-baseline justify-between mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500">Detay Tablo</div>
              <div className="text-lg font-bold text-slate-900 font-heading">Firma Toplamları</div>
            </div>
            <div className="text-xs text-slate-500">{detay.length} kayıt</div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-[0.2em] text-slate-500 border-b border-slate-200">
                  <th className="text-left py-2 font-bold">Firma</th>
                  <th className="text-right py-2 font-bold">Giriş</th>
                  <th className="text-right py-2 font-bold">Miktar</th>
                  <th className="text-right py-2 font-bold">Tutar</th>
                </tr>
              </thead>
              <tbody>
                {detay.map((r, i) => (
                  <tr key={r.supplier || `row-${i}`} className={i % 2 ? "bg-slate-50" : ""}>
                    <td className="py-2 font-medium">{r.supplier || "-"}</td>
                    <td className="py-2 text-right font-mono">{r.count}</td>
                    <td className="py-2 text-right font-mono">{r.qty}</td>
                    <td className="py-2 text-right font-mono">₺{(r.total || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                  </tr>
                ))}
                {detay.length === 0 && (
                  <tr><td colSpan={4} className="py-8 text-center text-slate-500">Veri yok.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </Shell>
  );
}
